<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Select from 'primevue/select';
import { useToast } from 'primevue/usetoast';
import { useWorkflowStore } from '@/stores/workflow.js';

const props = defineProps({
  entity: { type: String, required: true },
  id: { type: [Number, String], required: true },
  size: { type: String, default: 'small' },
});

const emit = defineEmits(['changed']);

const store = useWorkflowStore();
const toast = useToast();

const entId = computed(() => Number(props.id) || null);

const loading = computed(() => store.loading);
const available = computed(() => store.availableStates(props.entity, entId.value));
const currentStateFromStore = computed(() => store.currentState(props.entity, entId.value));

// Текущий статус — из current_state, а если его нет, то ищем в available по is_current
const current = computed(() => {
  if (currentStateFromStore.value) return currentStateFromStore.value;
  const fromAvailable = (available.value || []).find(x => x?.is_current);
  return fromAvailable || null;
});

// Опции: прошедшие (disabled), текущий (disabled), будущие разрешённые (enabled)
const options = computed(() => {
  const list = Array.isArray(available.value) ? available.value : [];
  return list.map(st => {
    const isPast = !!st?.is_passed;
    const isCur = !!st?.is_current;
    const isFuture = !!st?.is_future;
    const allowed = !!st?.is_allowed_forward;

    // Разрешаем выбирать только будущие разрешённые
    const section = isCur ? 'current' : (isPast ? 'past' : (isFuture ? 'future' : 'other'));
    const disabled = !(isFuture && allowed);

    return {
      label: st?.name || st?.code || String(st),
      value: st?.code || st?.name || String(st),
      obj: st,
      section,
      disabled,
      isFutureAllowed: isFuture && allowed,
    };
  });
});

const model = ref(null);

async function reload() {
  if (!props.entity || !entId.value) return;
  await store.loadAvailable(props.entity, entId.value); // историю больше не дергаем
}

onMounted(reload);
watch(() => [props.entity, entId.value], () => reload(), { immediate: false });

async function onChange(val) {
  if (!val) return;
  const selected = options.value.find(o => o.value === val);
  if (!selected || !selected.isFutureAllowed) return; // кликаем только будущие разрешённые
  try {
    await store.transition(props.entity, entId.value, { to_state: selected.value });
    toast.add({ severity: 'success', summary: 'Статус обновлён', life: 2000 });
    emit('changed', store.currentState(props.entity, entId.value));
    model.value = null; // сброс выбора
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Не удалось изменить статус', life: 3500 });
  }
}

const currentLabel = computed(() => current.value?.name || current.value?.code || 'Статус не задан');
</script>

<template>
  <Select
      v-model="model"
      :options="options"
      optionLabel="label"
      optionValue="value"
      optionDisabled="disabled"
      :disabled="loading || !Array.isArray(options) || options.length === 0"
      placeholder="Выберите следующий шаг"
      @update:modelValue="onChange"
      class="w-full"
  >
    <!-- Текущий статус показываем в value-слоте -->
    <template #value>
      <span class="text-gray-800">{{ currentLabel }}</span>
    </template>

    <!-- Визуальная дифференциация: прошлые — бледные, текущая — полужирная, будущие разрешённые — обычные -->
    <template #option="{ option }">
      <div
          class="flex items-center"
          :class="{
          'text-gray-400': option.section === 'past' || (option.section === 'future' && option.disabled),
          'font-medium text-gray-800': option.section === 'current',
          'text-gray-900': option.section === 'future' && !option.disabled,
        }"
      >
        <span>{{ option.label }}</span>
      </div>
    </template>
  </Select>
</template>

<style scoped>
/* ширина управляется классом w-full у Select */
</style>